import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import BASE_URL from "../../../../config/config";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiPlus, FiX, FiEdit2, FiTrash2, FiCpu, FiHardDrive } from "react-icons/fi";
import Loader from "../../../../components/Loader";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Popup from "../../../../components/Popup";
import { cabecerasAuth, claves } from "../../../../queries/cliente";
import Paginacion from '../../../../components/ui/Paginacion';

const TIPO_ESTADO = ["Activo", "En reparación", "No activo"];

const claseEstado = (estado) => {
  if (estado === "Activo") return "mat-badge-estado-activo";
  if (estado === "En reparación") return "mat-badge-estado-reparacion";
  return "mat-badge-estado-inactivo";
};

const FORM_VACIO = {
  marca: "",
  modelo: "",
  numero_serie: "",
  cpu: "",
  ram: "",
  almacenamiento: "",
  sistemaOperativo: "",
  estado: "Activo",
  personaAsignada: "",
  ubicacion: "",
  fechaCompra: "",
  proveedor: "",
  notas: "",
};

const EquiposManager = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const token = localStorage.getItem("AuthToken");
  const tokenInfo = token ? jwtDecode(token) : {};
  const isAdmin = tokenInfo?.role === "admin";


  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const [mostrarForm, setMostrarForm] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [editId, setEditId] = useState(null);

  const [borrarId, setBorrarId] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: token || "",
      "Content-Type": "application/json",
    }),
    [token]
  );

  const consultaEquipos = useQuery({
    queryKey: claves.equipos(),
    queryFn: async () => (await axios.get(`${BASE_URL}/equipos/`, cabecerasAuth())).data || [],
  });

  useEffect(() => {
    if (consultaEquipos.isError) toast.error(t('gestorEquipos.errorObtener'));
  }, [consultaEquipos.isError, t]);

  const equipos = consultaEquipos.data || [];
  const loading = consultaEquipos.isPending;

  const fetchEquipos = () => queryClient.invalidateQueries({ queryKey: claves.equipos() });

  const filtered = useMemo(() => {
    const q = searchText.toLowerCase();
    return equipos.filter((e) =>
      [
        e.marca,
        e.modelo,
        e.numero_serie,
        e.cpu,
        e.almacenamiento,
        e.sistemaOperativo,
        e.personaAsignada,
        e.ubicacion,
        e.proveedor,
        e.estado,
        e.notas,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [equipos, searchText]);

  const pageItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setForm(FORM_VACIO);
    setEditId(null);
  };

  const cerrarPanel = () => {
    setMostrarForm(false);
    resetForm();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      toast.error(t('gestorEquipos.accesoRestringido'));
      return;
    }

    try {
      if (editId) {
        await axios.put(`${BASE_URL}/equipos/${editId}`, form, { headers });
        toast.success(t('gestorEquipos.actualizadoOk'));
      } else {
        await axios.post(`${BASE_URL}/equipos/`, form, { headers });
        toast.success(t('gestorEquipos.creadoOk'));
      }
      await fetchEquipos();
      cerrarPanel();
    } catch (err) {
      console.error("Error guardando equipo:", err);
      toast.error(t('gestorEquipos.errorGuardar'));
    }
  };

  const onEdit = (item) => {
    setEditId(item.id);
    setForm({
      marca: item.marca ?? "",
      modelo: item.modelo ?? "",
      numero_serie: item.numero_serie ?? "",
      cpu: item.cpu ?? "",
      ram: item.ram ?? "",
      almacenamiento: item.almacenamiento ?? "",
      sistemaOperativo: item.sistemaOperativo ?? "",
      estado: item.estado ?? "Activo",
      personaAsignada: item.personaAsignada ?? "",
      ubicacion: item.ubicacion ?? "",
      fechaCompra: item.fechaCompra ?? "",
      proveedor: item.proveedor ?? "",
      notas: item.notas ?? "",
    });
    setMostrarForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pedirBorrado = (id) => {
    if (!isAdmin) {
      toast.error(t('gestorEquipos.accesoRestringido'));
      return;
    }
    setBorrarId(id);
  };

  const confirmarBorrado = async () => {
    const id = borrarId;
    setBorrarId(null);
    try {
      await axios.delete(`${BASE_URL}/equipos/${id}`, { headers });
      toast.success(t('gestorEquipos.eliminadoOk'));
      await fetchEquipos();
    } catch (err) {
      console.error("Error al eliminar equipo:", err);
      toast.error(t('gestorEquipos.errorEliminar'));
    }
  };

  if (!token) {
    return (
      <div className="site-width">
        <p>{t('gestorEquipos.iniciaSesion')}</p>
        <ToastContainer position="bottom-right" />
      </div>
    );
  }

  return (
    <main className="site-width">
      <ToastContainer position="bottom-right" />
      <div className="dashboard-content gap-equipos">
        <div className="pagina-cabecera">
          <h2>{t('gestorEquipos.titulo')}</h2>
          <p>{t('gestorEquipos.subtitulo')}</p>
        </div>

        {/* Barra de herramientas */}
        <div className="mat-toolbar">
          <input
            type="text"
            placeholder={t('gestorEquipos.buscarPlaceholder')}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
          />
          <div className="mat-toolbar-acciones">
            <select
              className="selector-page-size"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={15}>{t('gestorEquipos.porPagina', { n: 15 })}</option>
              <option value={30}>{t('gestorEquipos.porPagina', { n: 30 })}</option>
              <option value={60}>{t('gestorEquipos.porPagina', { n: 60 })}</option>
            </select>
            <button
              type="button"
              className={`chip-toggle ${mostrarForm ? 'activo' : ''}`}
              onClick={() => (mostrarForm ? cerrarPanel() : setMostrarForm(true))}
            >
              <FiPlus /> {t('gestorEquipos.nuevoEquipo')}
            </button>
          </div>
        </div>

        {/* Panel plegable: crear/editar equipo */}
        {mostrarForm && (
          <form className="mat-form-panel" onSubmit={onSubmit}>
            <div className="mat-form-panel-header">
              <h3>{editId ? t('gestorEquipos.editarEquipoNum', { id: editId }) : t('gestorEquipos.nuevoEquipo')}</h3>
              <button type="button" className="mat-close-btn" onClick={cerrarPanel} aria-label={t('gestorEquipos.cerrar')}>
                <FiX />
              </button>
            </div>

            <div className="mat-form-grid">
              <div className="mat-campo">
                <label>{t('gestorEquipos.marca')}</label>
                <input type="text" value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.modelo')}</label>
                <input type="text" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.numSerie')}</label>
                <input type="text" value={form.numero_serie} onChange={(e) => setForm({ ...form, numero_serie: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.cpu')}</label>
                <input type="text" value={form.cpu} onChange={(e) => setForm({ ...form, cpu: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.ramGb')}</label>
                <input type="number" min="0" value={form.ram} onChange={(e) => setForm({ ...form, ram: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.almacenamiento')}</label>
                <input type="text" value={form.almacenamiento} onChange={(e) => setForm({ ...form, almacenamiento: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.sistemaOperativo')}</label>
                <input type="text" value={form.sistemaOperativo} onChange={(e) => setForm({ ...form, sistemaOperativo: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.estado')}</label>
                <select value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} required>
                  {form.estado && !TIPO_ESTADO.includes(form.estado) && (
                    <option value={form.estado}>{form.estado} {t('gestorEquipos.actual')}</option>
                  )}
                  {TIPO_ESTADO.map((estado) => (
                    <option key={estado} value={estado}>{t(`gestorEquipos.estados.${estado}`)}</option>
                  ))}
                </select>
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.fechaCompra')}</label>
                <input type="date" value={form.fechaCompra} onChange={(e) => setForm({ ...form, fechaCompra: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.personaAsignada')}</label>
                <input type="text" value={form.personaAsignada} onChange={(e) => setForm({ ...form, personaAsignada: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.ubicacion')}</label>
                <input type="text" value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorEquipos.proveedor')}</label>
                <input type="text" value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} />
              </div>

              <div className="mat-campo mat-campo-full">
                <label>{t('gestorEquipos.notas')}</label>
                <textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
              </div>
            </div>

            <div className="mat-form-actions">
              <button type="button" className="mat-btn-secundario" onClick={resetForm}>{t('gestorEquipos.limpiar')}</button>
              <button type="submit" className="mat-btn-primario">
                {editId ? t('gestorEquipos.actualizarEquipo') : t('gestorEquipos.anadirEquipo')}
              </button>
            </div>
          </form>
        )}

        {/* Listado */}
        {loading ? (
          <div className="loader-fullscreen"><Loader /></div>
        ) : (
          <div className="overflow-tabla">
            <div className="mat-panel">
              <div className="mat-list-header mat-grid-equipos">
                <span>{t('gestorEquipos.marcaModelo')}</span>
                <span>{t('gestorEquipos.numSerie')}</span>
                <span>{t('gestorEquipos.especificaciones')}</span>
                <span>{t('gestorEquipos.usuario')}</span>
                <span>{t('gestorEquipos.ubicacion')}</span>
                <span>{t('gestorEquipos.estado')}</span>
                <span>{t('gestorEquipos.acciones')}</span>
              </div>

              <div className="mat-rows">
                {pageItems.length === 0 ? (
                  <div className="mat-vacio">{t('gestorEquipos.sinCoincidencias')}</div>
                ) : (
                  pageItems.map((e) => (
                    <div key={e.id} className="mat-row mat-grid-equipos">
                      <div className="mat-col">
                        <span className="mat-col-titulo">{e.marca} {e.modelo}</span>
                      </div>
                      <div className="mat-col">
                        <code className="mat-serie">{e.numero_serie || '—'}</code>
                      </div>
                      <div className="mat-col mat-col-specs">
                        {e.cpu && <span><FiCpu /> {e.cpu}</span>}
                        {(e.ram || e.almacenamiento) && (
                          <span><FiHardDrive /> {[e.ram && `${e.ram}GB RAM`, e.almacenamiento].filter(Boolean).join(' · ')}</span>
                        )}
                        {e.sistemaOperativo && <span className="mat-col-so">{e.sistemaOperativo}</span>}
                      </div>
                      <div className="mat-col">{e.personaAsignada || '—'}</div>
                      <div className="mat-col">{e.ubicacion || '—'}</div>
                      <div className="mat-col">
                        <span className={`mat-badge-estado ${claseEstado(e.estado)}`}>{t(`gestorEquipos.estados.${e.estado}`, { defaultValue: e.estado })}</span>
                      </div>
                      <div className="mat-col mat-col-acciones">
                        <button className="mat-btn-icono" onClick={() => onEdit(e)} title={t('gestorEquipos.editar')}>
                          <FiEdit2 />
                        </button>
                        <button className="mat-btn-icono mat-btn-icono-danger" onClick={() => pedirBorrado(e.id)} title={t('gestorEquipos.eliminar')}>
                          <FiTrash2 />
                        </button>
                        <Link to={`/dashboard/equipos/${e.id}/perifericos`} className="mat-btn-icono" title={t('gestorEquipos.verPerifericos')}>
                          <FiHardDrive />
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        <Paginacion
          pagina={currentPage}
          porPagina={itemsPerPage}
          total={filtered.length}
          onCambiar={setCurrentPage}
        />
      </div>

      <Popup
        open={!!borrarId}
        title={t('gestorEquipos.eliminarEquipo')}
        onClose={() => setBorrarId(null)}
        showConfirm={false}
        footerActions={(
          <button className="boton-aceptar-popup" onClick={confirmarBorrado}>{t('gestorEquipos.eliminar')}</button>
        )}
      >
        <p>{t('gestorEquipos.confirmarEliminar')}</p>
      </Popup>
    </main>
  );
};

export default EquiposManager;
